/**
 * Dashboard Context and Hook
 *
 * Provides dashboard state management and operations
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ComponentType,
  type ReactNode,
} from 'react';
import type {
  DashboardLayout,
  DashboardWidgetConfig,
  DashboardContextValue,
} from '../types/dashboard';
import { findNextAvailablePosition } from '../layouts/DashboardGrid';

/**
 * Dashboard Context
 */
const DashboardContext = createContext<DashboardContextValue | null>(null);

/**
 * Dashboard Provider Props
 */
export interface DashboardProviderProps {
  /**
   * Initial layouts
   */
  initialLayouts?: DashboardLayout[];

  /**
   * Default layout ID
   */
  defaultLayoutId?: string;

  /**
   * Callback when layout is saved
   */
  onSaveLayout?: (layout: DashboardLayout) => Promise<void>;

  /**
   * Children
   */
  children: ReactNode;
}

/**
 * Default dashboard layout
 */
const createDefaultLayout = (): DashboardLayout => ({
  id: 'default',
  name: 'Default Dashboard',
  description: 'Default dashboard layout',
  widgets: [],
  columns: 12,
  rowHeight: 100,
  gap: 16,
  isDefault: true,
});

/**
 * Dashboard Provider Component
 *
 * Provides dashboard context to children
 */
export const DashboardProvider: ComponentType<DashboardProviderProps> = ({
  initialLayouts = [createDefaultLayout()],
  defaultLayoutId = 'default',
  onSaveLayout,
  children,
}) => {
  const [layouts, setLayouts] = useState<DashboardLayout[]>(initialLayouts);
  const [currentLayoutId, setCurrentLayoutId] = useState<string>(defaultLayoutId);
  const [editMode, setEditMode] = useState<boolean>(false);

  // Get current layout
  const layout =
    layouts.find((l) => l.id === currentLayoutId) || createDefaultLayout();

  // Switch to a different layout
  const switchLayout = useCallback((layoutId: string) => {
    setCurrentLayoutId(layoutId);
    setEditMode(false);
  }, []);

  // Add widget to current layout
  const addWidget = useCallback(
    (widget: DashboardWidgetConfig) => {
      setLayouts((prevLayouts) =>
        prevLayouts.map((l) => {
          if (l.id !== currentLayoutId) return l;

          // Find next available position if not specified
          const position =
            widget.position.col === 0 && widget.position.row === 0
              ? findNextAvailablePosition(l, widget.size)
              : widget.position;

          return {
            ...l,
            widgets: [
              ...l.widgets,
              {
                ...widget,
                position,
              },
            ],
          };
        })
      );
    },
    [currentLayoutId]
  );

  // Remove widget from current layout
  const removeWidget = useCallback(
    (widgetId: string) => {
      setLayouts((prevLayouts) =>
        prevLayouts.map((l) => {
          if (l.id !== currentLayoutId) return l;

          return {
            ...l,
            widgets: l.widgets.filter((w) => w.id !== widgetId),
          };
        })
      );
    },
    [currentLayoutId]
  );

  // Update widget configuration
  const updateWidget = useCallback(
    (widgetId: string, updates: Partial<DashboardWidgetConfig>) => {
      setLayouts((prevLayouts) =>
        prevLayouts.map((l) => {
          if (l.id !== currentLayoutId) return l;

          return {
            ...l,
            widgets: l.widgets.map((w) =>
              w.id === widgetId ? { ...w, ...updates } : w
            ),
          };
        })
      );
    },
    [currentLayoutId]
  );

  // Save current layout
  const saveLayout = useCallback(async () => {
    if (onSaveLayout) {
      await onSaveLayout(layout);
    }
    console.log('[Dashboard] Layout saved:', layout.id);
  }, [layout, onSaveLayout]);

  // Reset layout to default
  const resetLayout = useCallback(() => {
    setLayouts((prevLayouts) =>
      prevLayouts.map((l) => {
        if (l.id !== currentLayoutId) return l;

        const defaultLayout = initialLayouts.find((il) => il.id === l.id);
        return defaultLayout || l;
      })
    );
    setEditMode(false);
  }, [currentLayoutId, initialLayouts]);

  const contextValue: DashboardContextValue = {
    layout,
    layouts,
    switchLayout,
    addWidget,
    removeWidget,
    updateWidget,
    saveLayout,
    resetLayout,
    editMode,
    setEditMode,
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

/**
 * Use Dashboard Hook
 *
 * Access dashboard context
 *
 * @returns Dashboard context value
 */
export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }

  return context;
};
